/**
 * StatusBadge — outline-style status/priority pill used in every data
 * table across the app.
 *
 * Phase-1 of the design refresh: replaces the full-saturation filled
 * badges that crowded admin tables (Completed/Cancelled/Pending/
 * Paid/Failed/Low/Normal/High/Highest all simultaneously fighting for
 * attention) with a calmer 1-px outline + transparent fill aesthetic
 * inspired by Linear / Stripe.
 *
 * Usage:
 *   <StatusBadge tone="success">Completed</StatusBadge>
 *   <StatusBadge tone="danger" size="sm">Cancelled</StatusBadge>
 *   <StatusBadge tone="warn"  dot>High priority</StatusBadge>
 *
 * Tones map to brand-neutral semantic intents — caller decides which
 * domain word ("Completed", "Paid", "In-progress") maps to which tone.
 */
import React from 'react';

const TONES = {
  success: 'border-emerald-200 text-emerald-700 bg-emerald-50/40',
  warn:    'border-amber-200   text-amber-700   bg-amber-50/40',
  danger:  'border-rose-200    text-rose-700    bg-rose-50/40',
  info:    'border-cyan-200    text-cyan-700    bg-cyan-50/40',
  neutral: 'border-slate-200   text-slate-600   bg-white',
};

const SIZES = {
  sm: 'px-1.5 py-0.5 text-[10px] gap-1',
  md: 'px-2   py-0.5 text-[11px] gap-1.5',
  lg: 'px-2.5 py-1   text-xs    gap-1.5',
};

const DOT_TONES = {
  success: 'bg-emerald-500',
  warn:    'bg-amber-500',
  danger:  'bg-rose-500',
  info:    'bg-cyan-500',
  neutral: 'bg-slate-400',
};

export default function StatusBadge({
  children,
  tone = 'neutral',
  size = 'md',
  dot = false,
  className = '',
  testid,
}) {
  const toneClass = TONES[tone] || TONES.neutral;
  const sizeClass = SIZES[size] || SIZES.md;
  return (
    <span
      className={`inline-flex items-center font-medium rounded-md border whitespace-nowrap ${sizeClass} ${toneClass} ${className}`}
      data-testid={testid}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${DOT_TONES[tone] || DOT_TONES.neutral}`} />}
      {children}
    </span>
  );
}

/** Map a free-text status string to one of our semantic tones.
 *  Centralised so every table uses the same lookup. */
export function toneForStatus(status) {
  const s = String(status || '').toLowerCase();
  if (['completed', 'paid', 'success', 'active', 'approved', 'delivered'].includes(s)) return 'success';
  if (['cancelled', 'canceled', 'failed', 'rejected', 'error', 'refunded'].includes(s)) return 'danger';
  if (['pending', 'in_review', 'on_hold', 'processing', 'awaiting'].includes(s))        return 'warn';
  if (['in_progress', 'in-progress', 'queued', 'draft', 'new'].includes(s))             return 'info';
  return 'neutral';
}

/** Map a priority level to one of our tones. */
export function toneForPriority(priority) {
  const p = String(priority || '').toLowerCase();
  if (p === 'highest' || p === 'urgent') return 'danger';
  if (p === 'high')                      return 'warn';
  if (p === 'low')                       return 'success';
  return 'neutral';
}
